import { useCallback, useContext } from "react";
import { Trans, useTranslation } from "react-i18next";
import { ProjectDownloadIcon } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/components/JointBillTable/components/ProjectDownloadIcon/ProjectDownloadIcon";
import { JointBillContext } from "@features/JointBillList/utils";
import { type ProjectTypeId } from "@features/Login/types";
import { Dropdown } from "@components/Dropdown/Dropdown";
import { IconButton } from "@components/IconButton/IconButton";
import {
  DotsHorizontalIcon,
  DownloadIcon,
  ExclamationCircleIcon,
  PencilIcon,
} from "@components/icons";
import { Checkbox } from "@components/inputs/Checkbox/Checkbox";
import { SelectionState } from "@services/overview/types";
import { formatPrice } from "@utils/units";

interface IJointBillHeaderTotalCellProps {
  paidAmount?: number;
  unpaidAmount?: number;
  onChangePlanClick?: () => void;
  jointBillSelectionState?: SelectionState;
  onJointBillCheckboxClick?: () => void;
  isJointBillCheckboxDisabled?: boolean;
  isJointBillCheckboxLoading?: boolean;
  isEditHouseLoading?: boolean;
  onEditHouseClick?: () => void;
  isBillDownloadEnabled?: boolean;
  onBillDownloadClick?: () => void;
  isBillDownloadLoading?: boolean;
  projectTypeId?: ProjectTypeId;
  isProjectBillDownloadEnabled?: boolean;
  onProjectBillDownloadClick?: () => void;
  isProjectBillDownloadLoading?: boolean;
  isOpenPeriodButtonEnabled?: boolean;
  onOpenPeriodButtonClick?: () => void;
}

export const JointBillHeaderTotalCell: React.FC<
  IJointBillHeaderTotalCellProps
> = ({
  paidAmount = 0,
  unpaidAmount = 0,
  onChangePlanClick,
  jointBillSelectionState,
  onJointBillCheckboxClick,
  isJointBillCheckboxDisabled,
  isJointBillCheckboxLoading,
  isEditHouseLoading,
  onEditHouseClick,
  isBillDownloadEnabled,
  onBillDownloadClick,
  isBillDownloadLoading,
  projectTypeId,
  isProjectBillDownloadEnabled,
  onProjectBillDownloadClick,
  isProjectBillDownloadLoading,
  isOpenPeriodButtonEnabled,
  onOpenPeriodButtonClick,
}) => {
  const { t } = useTranslation();
  const { isSummaryMode, isArchiveMode } = useContext(JointBillContext);

  const dropdownButton = useCallback(
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    (props) => (
      <IconButton
        icon={DotsHorizontalIcon}
        iconClassName="text-base-black"
        {...props}
      />
    ),
    [],
  );

  const dropdownOptions = [
    {
      label: t("bill.interactions.editAddress.label"),
      onClick: onEditHouseClick,
      icon: PencilIcon,
      disabled: isEditHouseLoading,
    },
    {
      label: t("bill.interactions.changePlan.label"),
      onClick: onChangePlanClick,
      icon: PencilIcon,
    },
    ...(isOpenPeriodButtonEnabled
      ? [
          {
            label: t("bill.interactions.openPeriod.label"),
            onClick: onOpenPeriodButtonClick,
            icon: ExclamationCircleIcon,
          },
        ]
      : []),
    ...(isBillDownloadEnabled
      ? [
          {
            label: t("bill.interactions.downloadBill.label"),
            onClick: onBillDownloadClick,
            icon: DownloadIcon,
            disabled: isBillDownloadLoading,
          },
        ]
      : []),
    ...(isProjectBillDownloadEnabled
      ? [
          {
            label: t(
              `bill.interactions.downloadProjectBill.labels.${projectTypeId}`,
            ),
            onClick: onProjectBillDownloadClick,
            // @ts-expect-error - c
            icon: (props) => (
              <ProjectDownloadIcon projectTypeId={projectTypeId} {...props} />
            ),
            disabled: isProjectBillDownloadLoading,
          },
        ]
      : []),
  ];

  return (
    <div className="mb-auto flex flex-col items-center gap-3 md:mb-0 md:flex-row md:gap-4">
      <div className="hidden flex-col items-end whitespace-nowrap text-grey-dark md:flex">
        <span>
          <Trans
            i18nKey="bill.totalPaid"
            values={{
              amount: formatPrice(paidAmount),
            }}
            components={{
              b: <span className="font-semibold text-base-black" />,
            }}
          />
        </span>
        <span>
          <Trans
            i18nKey={isArchiveMode ? "bill.remainingAmount" : "bill.leftToPay"}
            values={{
              amount: formatPrice(unpaidAmount),
            }}
            components={{
              b: <span className="font-semibold text-base-black" />,
            }}
          />
        </span>
      </div>
      {!isSummaryMode && (
        <div className="flex gap-3">
          <div className="md:hidden">
            <Dropdown
              buttonAs={dropdownButton}
              options={dropdownOptions}
              placement="bottom-end"
              ariaLabel={t("ariaLabel.menu")}
              minContainerWidth={320}
            />
          </div>
          <Checkbox
            checked={jointBillSelectionState === SelectionState.SELECTED}
            isIndeterminate={
              jointBillSelectionState === SelectionState.PARTIALLY_SELECTED
            }
            onChange={onJointBillCheckboxClick}
            disabled={isJointBillCheckboxDisabled}
            isLoading={isJointBillCheckboxLoading}
            aria-label={t("label.jointBillHeaderTotalCheckbox")}
          />
        </div>
      )}
    </div>
  );
};
